import { Component, computed, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { of } from 'rxjs';
import { AuthenticationService } from '../../../authentication/authentication.service';
import { FirebaseEventService } from '../../../authentication/firebase.event.service';
import { CableLibraryDto, CableRangeDto, CableSourceDto, ManufacturerDto, MceApiService } from '../../../core/mceapi.client.generated';
import { FormImage, FormImageComponent } from '../../../shared/components/form-image/form-image.component';
import { distinctBy } from '../../../shared/functions/array.extensions';
import { EditCable } from '../cable-edit.model';
import { createSystemVoltageDto } from '../system-voltage/system-voltage.model';

type ManufacturerWithGuid = ManufacturerDto & { guid: string };
type CableSourceWithManufacturer = CableSourceDto & {
  guid: string;
  manufacturer: ManufacturerWithGuid;
};
type CableRangeWithSource = CableRangeDto & { source: CableSourceWithManufacturer };
type CableLibraryWithGuid = CableLibraryDto & { guid: string };

function hasCompleteSource(cable: CableRangeDto): cable is CableRangeWithSource {
  return !!cable.source?.guid && !!cable.source.manufacturer?.guid;
}

function hasLibraryGuid(library: CableLibraryDto): library is CableLibraryWithGuid {
  return !!library.guid;
}


@Component({
  selector: 'cable-edit-selection',
  imports: [FormField, MatSelectModule, MatCheckboxModule, MatTooltipModule, FormImageComponent],
  templateUrl: './cable-selection.component.html',
})

export class CableSelectionComponent {

  readonly form = input.required<FieldTree<EditCable>>();

  private mceApiService = inject(MceApiService);
  private authenticationService = inject(AuthenticationService);
  private firebaseEventService = inject(FirebaseEventService);

  cablesRx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      disableVoltageFilter: this.form().settings.disableVoltageFilter().value(),
    }),
    stream: ({ params }) =>
      this.form().systemVoltage().valid()
        ? this.mceApiService.v1CableLibraryRecommend(params.disableVoltageFilter, params.systemVoltage)
        : of([])
  });
  cables = computed(() => this.cablesRx.value() ?? [] as CableRangeDto[]);

  selects = computed(() => {
    const cables = this.cables().filter(hasCompleteSource);

    const manufacturers =
      distinctBy(cables.map(cable => cable.source.manufacturer), m => m.guid);

    const sources =
      cables
        .filter(cable => cable.source.manufacturer.guid === this.form().selectedCable.manufacturerGuid().value());

    const libraries = (sources
      .find(cable => cable.source.guid === this.form().selectedCable.sourceGuid().value())
      ?.library ?? [])
      .filter(hasLibraryGuid);

    const csas =
      cables
        .find(cable => cable.source.guid === this.form().selectedCable.sourceGuid().value())
        ?.library?.find(lib => lib.guid === this.form().selectedCable.libraryGuid().value())
        ?.csa ?? [];

     return ({
       manufacturers: manufacturers,
       sources: sources,
       libraries: libraries,
       csas: csas
      })


  });

  _resetGuids = effect(() => {
    const manufacturers = this.selects().manufacturers;
    const sources = this.selects().sources;
    const libraries = this.selects().libraries;
    const csas = this.selects().csas;

    if (manufacturers.length > 0 && manufacturers.findIndex(m => m.guid === this.form().selectedCable.manufacturerGuid().value()) === -1) { 
    this.form().selectedCable.manufacturerGuid().value.set(manufacturers[0].guid);
      return;
    }

    if (sources.length > 0 && sources.findIndex(s => s.source.guid === this.form().selectedCable.sourceGuid().value()) === -1) {
      this.form().selectedCable.sourceGuid().value.set(sources[0].source.guid);
      return;
    }

    if (libraries.length > 0 && libraries.findIndex(l => l.guid === this.form().selectedCable.libraryGuid().value()) === -1) {
      this.form().selectedCable.libraryGuid().value.set(libraries[0].guid);
    }

    if (csas.length > 0 && csas.findIndex(c => c === this.form().selectedCable.csa().value()) === -1) {
      this.form().selectedCable.csa().value.set(csas[0]);
    }

  });

  onManufacturerSelected(manufacturerGuid: string): void {
    const cableUid = this.form().description.uid().value();
    this.firebaseEventService.logCableSelectManufacturerEvent(this.authenticationService.currentUser(), cableUid, manufacturerGuid);
  }

  viewState = computed(() => ({
    logo: ({
      url: this.selects().manufacturers.filter(m => m.guid === this.form().selectedCable.manufacturerGuid().value())[0]?.logoUrl ?? '',
      alt: 'Manufacturer Logo',
    } as FormImage)
  }));

}


